import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import AuthLayout from './auth/AuthLayout';
import Button from '@/components/form/Button';
import Input from '@/components/form/Input';
import { UserIcon, MailIcon, KeyIcon, ShieldCheckIcon } from 'lucide-react';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for redirect SNAP ID in URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirectId = params.get('redirect');
    
    if (redirectId) {
      localStorage.setItem('redirectSnapId', redirectId);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!username.trim()) {
      toast.error('Username is required');
      return;
    }
    
    if (username.length < 3 || username.length > 30) {
      toast.error('Username must be between 3 and 30 characters');
      return;
    }
    
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      toast.error('Unsername must be 3-30 chars [a-z, A-Z, 0-9, "-", "_"]');
      return;
    }
    
    if (!password || password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!acceptedTerms) {
      toast.error('You must accept the Conditions of Participation to register');
      return;
    }

    try {
      setLoading(true);
      
      // First check if username is available
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username)
        .maybeSingle();
      
      if (existingUser) {
        toast.error('Username is already taken');
        setLoading(false);
        return;
      }

      // Sign up the user with simplified approach
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          data: {
            username // Store username in user metadata
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        if (error.message.includes('User already registered')) {
          throw new Error('An account with this email already exists');
        }
        throw error;
      }
      
      if (!data.user) {
        throw new Error('Failed to create account');
      }

      // Check if there's a redirect SNAP ID in localStorage
      const redirectSnapId = localStorage.getItem('redirectSnapId');
      
      toast.success('Registration successful! Please check your email to confirm your account.');
      
      // Navigate to login page with a query parameter if there's a redirect SNAP ID
      if (redirectSnapId) {
        navigate(`/login?redirect=${redirectSnapId}`);
      } else {
        navigate('/login');
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mb-8 text-center">
        <span className="inline-block bg-yellow-100 text-yellow-800 text-sm font-medium px-4 py-2 rounded-full shadow-sm">
          🌟 Be one of the first to try SnapVote – now available in Early Access.
        </span>
      </div>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700">
            Username
          </label>
          <div className="mt-1">
            <Input
              id="username"
              name="username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={3}
              maxLength={30}
              icon={<UserIcon className="h-5 w-5" />}
              disabled={loading}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            3-30 characters [a-z, A-Z, 0-9, "-", "_"]
          </p>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email address
          </label>
          <div className="mt-1">
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              icon={<MailIcon className="h-5 w-5" />}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="mt-1">
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={<KeyIcon className="h-5 w-5" />}
              disabled={loading}
              minLength={6}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Must be at least 6 characters long
          </p>
        </div>

        <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
          <div className="flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <ShieldCheckIcon className="h-5 w-5 text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Age Verification Required:</strong><br/> You must be at least 18 years old to participate in <b>CashSNAPs</b>.<br/> Age-verification will be required before accessing this feature.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="terms"
              name="terms"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="terms" className="font-medium text-gray-700">
              I accept the <Link to="/conditions" className="text-primary-600 hover:text-primary-500 underline" target="_blank">Conditions of Participation</Link>
            </label>
            <p className="text-gray-500">
              Including early voting bonus system for CashSNAPs
            </p>
          </div>
        </div>

        <div>
          <Button
            type="submit"
            className="w-full"
            loading={loading}
          >
            Sign up
          </Button>
        </div>

        <div className="text-sm text-center">
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}